import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { Award, Code2 } from 'lucide-react';

const CrewSection = () => {
  const { t } = useLanguage();

  const teamMembers = [
    {
      name: 'Mario Pino Corona',
      role: 'FOUNDER / CEO',
      image: '/mario_pino.jpg',
      icon: Award,
    },
    {
      name: 'Rodrigo Mancilla',
      role: 'CTO',
      image: '/rodrigo_mancilla.jpg',
      icon: Code2,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="crew" className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-cms="crew.title">
            {t('crew.title')}
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto" data-cms="crew.subtitle">
            {t('crew.subtitle')}
          </p>
        </motion.div>

        {/* Team Grid - 2 members centered */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-3xl mx-auto mb-16"
        >
          {teamMembers.map((member, index) => {
            const IconComponent = member.icon;
            return (
              <motion.div
                key={member.name}
                variants={itemVariants}
                className="group"
              >
                <div className="relative bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-blue-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/10">
                  {/* Image Container */}
                  <div className="relative mb-6 overflow-hidden rounded-xl">
                    <div className="aspect-[3/4] relative">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-top transform group-hover:scale-105 transition-transform duration-700 grayscale"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                    </div>
                    
                    {/* Icon Badge */}
                    <div className="absolute top-4 right-4 w-12 h-12 bg-blue-600/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                      {member.name}
                    </h3>
                    <p className="text-blue-400 font-medium text-sm uppercase tracking-wider">
                      {member.role}
                    </p>
                  </div>

                  {/* Decorative Line */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 group-hover:w-3/4 transition-all duration-500 rounded-full" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Team Description */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative"
        >
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 lg:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Values */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white mb-4" data-cms="crew.valuesTitle">
                  {t('crew.valuesTitle')}
                </h3>
                <div className="space-y-4">
                  {t('crew.values').map((value, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                      <p className="text-slate-300">{value}</p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Quote */}
              <div className="relative">
                <div className="absolute -top-4 -left-4 text-6xl text-blue-500/20 font-serif">"</div>
                <blockquote className="relative pl-6 border-l-2 border-blue-500">
                  <p className="text-lg text-slate-300 italic mb-4" data-cms="crew.quote">
                    {t('crew.quote')}
                  </p>
                  <cite className="text-sm text-slate-500 not-italic" data-cms="crew.quoteAuthor">
                    — {t('crew.quoteAuthor')}
                  </cite>
                </blockquote>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CrewSection;
